import { Grid, Skeleton } from '@mui/material';
import NumberFormat from 'react-number-format';
import { useNavigate, useParams } from 'react-router-dom';
import {
	CerebroBreadcrumb,
	CerebroHeading,
	CerebroLink,
	CerebroPageContainer,
	CerebroText,
} from '../shared/components';
import { Urls } from '../shared/context';
import { useDataRoomQuery, useDataRoomSummaryQuery } from '../shared/hooks';
import { DateUtils } from '../shared/utils';

export const Summary: React.FC = () => {
	const { guid } = useParams<'guid'>();
	const navigate = useNavigate();

	const dataRoomQuery = useDataRoomQuery(guid);
	const summaryQuery = useDataRoomSummaryQuery(guid);

	return (
		<CerebroPageContainer maxWidth="100%">
			<CerebroBreadcrumb>Data Room Summary</CerebroBreadcrumb>

			{dataRoomQuery.isLoading ||
				(summaryQuery.isLoading && (
					<>
						<Skeleton height={80} />
						<Skeleton height={80} />
						<Skeleton height={80} />
					</>
				))}

			{summaryQuery.isSuccess && (
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<CerebroHeading size="h2" component="h2">
							Deal
						</CerebroHeading>
					</Grid>

					<SummaryDisplayRow
						label={'Deal Name'}
						text={summaryQuery.data.dealName}
						displayType="text"
						comments={null}
					/>
					<SummaryDisplayRow
						label={'Total Requested Amount'}
						text={summaryQuery.data.totalRequestedAmount?.toString()}
						displayType="currency"
						comments={null}
					/>
					{summaryQuery.data.isAsapTargetDate ? (
						<SummaryDisplayRow
							label={'Target Funding Date'}
							text={'ASAP'}
							displayType="text"
							comments={null}
						/>
					) : (
						<SummaryDisplayRow
							label={'Target Funding Date'}
							text={summaryQuery.data.targetFundingDate?.toString()}
							displayType="date"
							comments={null}
						/>
					)}

					<Grid item xs={12}>
						<CerebroHeading size="h2" component="h2">
							<CerebroLink
								variant="large"
								sx={{ cursor: 'pointer' }}
								onClick={() => navigate(`/${guid}/${Urls.Milestones.Root}`)}
							>
								Lender Status
							</CerebroLink>
						</CerebroHeading>
					</Grid>
					<SummaryDisplayRow
						label={'# Invited'}
						text={summaryQuery.data.institutionLendersInvited.toString()}
						displayType="text"
						comments={'# of lenders invited'}
					/>
					<SummaryDisplayRow
						label={'# Accepted'}
						text={summaryQuery.data.institutionLendersAccepted.toString()}
						displayType="text"
						comments={'# lenders accepting invitation (teaser or directly adding to data room)'}
					/>
					<SummaryDisplayRow
						label={'# Pending'}
						text={summaryQuery.data.institutionLendersPending.toString()}
						displayType="text"
						comments={'# invitations not responded to'}
					/>
					<SummaryDisplayRow
						label={'# Accessed'}
						text={summaryQuery.data.institutionLendersAccessed.toString()}
						displayType="text"
						comments={'# lenders that accessed the data room'}
					/>
					<SummaryDisplayRow
						label={'# Declined/Opted Out'}
						text={summaryQuery.data.institutionLendersDeclined.toString()}
						displayType="text"
						comments={'# lenders that declined teaser or opted out of the data room'}
					/>
					<SummaryDisplayRow
						label={'# Active'}
						text={summaryQuery.data.institutionLendersActive.toString()}
						displayType="text"
						comments={'# lenders currently active in data room'}
					/>

					<Grid item xs={12}>
						<CerebroHeading size="h2" component="h2">
							<CerebroLink
								variant="large"
								sx={{ cursor: 'pointer' }}
								onClick={() => navigate(`/${guid}/${Urls.DiligenceRequests.Root}`)}
							>
								Diligence Requests
							</CerebroLink>
						</CerebroHeading>
					</Grid>
					<SummaryDisplayRow
						label={'# Created'}
						text={summaryQuery.data.totalDiligenceRequests.toString()}
						displayType="text"
						comments={null}
					/>
					<SummaryDisplayRow
						label={'# Open'}
						text={summaryQuery.data.diligenceRequestsOpen.toString()}
						displayType="text"
						comments={null}
					/>
					<SummaryDisplayRow
						label={'# Closed'}
						text={summaryQuery.data.diligenceRequestsClosed.toString()}
						displayType="text"
						comments={''}
					/>

					<Grid item xs={12}>
						<CerebroHeading size="h2" component="h2">
							<CerebroLink
								variant="large"
								sx={{ cursor: 'pointer' }}
								onClick={() => navigate(`/${guid}/${Urls.TermSheets.Root}`)}
							>
								Term Sheets
							</CerebroLink>
						</CerebroHeading>
					</Grid>
					<SummaryDisplayRow
						label={'# Submitted Term Sheets'}
						text={summaryQuery.data.totalTermSheets.toString()}
						displayType="text"
						comments={null}
					/>

					<Grid item xs={12}>
						<CerebroHeading size="h2" component="h2">
							<CerebroLink
								variant="large"
								sx={{ cursor: 'pointer' }}
								onClick={() => navigate(`/${guid}/${Urls.Documents.Root}`)}
							>
								Documents
							</CerebroLink>
						</CerebroHeading>
					</Grid>
					<SummaryDisplayRow
						label={'# Available Documents'}
						text={summaryQuery.data.totalDocuments.toString()}
						displayType="text"
						comments={null}
					/>
					<SummaryDisplayRow
						label={'Last Document Uploaded'}
						text={summaryQuery.data.lastDocumentUploaded?.toString()}
						displayType="date"
						comments={null}
					/>
				</Grid>
			)}
		</CerebroPageContainer>
	);
};

interface SummaryDisplayRowProps {
	label: string;
	text: string | undefined | null;
	displayType: SummaryDisplayType;
	comments: string | null;
}
const SummaryDisplayRow: React.FC<SummaryDisplayRowProps> = ({ label, text, displayType, comments }) => {
	return (
		<>
			<Grid item xs={1}></Grid>
			<Grid item xs={4} md={3}>
				<CerebroText sx={{ fontWeight: 'bold' }}>{label}</CerebroText>
			</Grid>
			<Grid item xs={2} md={2}>
				{displayType == 'currency' && (
					<NumberFormat value={text} displayType="text" thousandSeparator={true} prefix="$" />
				)}
				{displayType == 'text' && <CerebroText>{text}</CerebroText>}
				{displayType == 'date' && <CerebroText>{DateUtils.FormatStringAsShortDate(text)}</CerebroText>}
			</Grid>
			<Grid item xs={5} md={6} alignSelf="flex-end">
				{comments ? <CerebroText variant="hint">{comments}</CerebroText> : <></>}
			</Grid>
		</>
	);
};
type SummaryDisplayType = 'text' | 'currency' | 'date' | 'status';
